// ---------------------------------------------------------------------------
// Menu ☰ do app (celular): gaveta que abre pela direita, sempre por cima de
// tudo, com grupos que só aparecem quando fazem sentido para a pessoa.
// Renderiza no body (portal), trava a rolagem do fundo e respeita a área
// segura de baixo (o último item aparece inteiro).
//
// O estado vive num contexto (useMenuApp) para a barra de cima, o Início e os
// cabeçalhos das telas internas poderem abrir o menu. Não toca no banco.
// ---------------------------------------------------------------------------
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useConvites } from '@/pages/convites/context';
import { useAcessoApp } from '@/hooks/useAcessoApp';
import { emApp } from '@/lib/app';
import { iniciaisNome } from '@/pages/loja/utils';
import Sheet from '@/components/ui/Sheet';
import Modal from '@/components/ui/Modal';
import AvatarPessoa from '@/components/ui/AvatarPessoa';
import AvisosCelular from '@/components/feature/AvisosCelular';
import EntrarNaFuncao from '@/components/feature/EntrarNaFuncao';
import { FolhaTenhoCodigo } from '@/components/feature/TenhoCodigo';
import { temOracaoNova } from '@/lib/oracaoVista';

const CHAVE_NAO_LIDOS = 'seliga:avisos_nao_lidos';

function lerNaoLidos(): number {
  try {
    return Math.max(0, Number(localStorage.getItem(CHAVE_NAO_LIDOS) || '0') || 0);
  } catch {
    return 0;
  }
}

function gravarNaoLidos(n: number): void {
  try {
    localStorage.setItem(CHAVE_NAO_LIDOS, String(Math.max(0, n)));
  } catch {
    /* armazenamento indisponível */
  }
}

interface MenuAppCtx {
  aberto: boolean;
  abrir: () => void;
  fechar: () => void;
  logado: boolean;
  organiza: boolean;
  primeiroNome: string;
  iniciais: string;
  naoLidos: number;
}

const Contexto = createContext<MenuAppCtx>({
  aberto: false,
  abrir: () => {},
  fechar: () => {},
  logado: false,
  organiza: false,
  primeiroNome: '',
  iniciais: '',
  naoLidos: 0,
});

export function useMenuApp(): MenuAppCtx {
  return useContext(Contexto);
}

interface ItemMenu {
  rotulo: string;
  icone: string;
  para?: string;
  aoTocar?: () => void;
  perigo?: boolean;
  selo?: string;
  /** Ponto vermelho de novidade (oração do dia ainda não aberta). */
  novidade?: boolean;
}

interface GrupoMenu {
  titulo: string;
  itens: ItemMenu[];
}

const ITEM_BASE =
  'aperta flex min-h-[52px] w-full items-center gap-3 rounded-[12px] px-3 text-left text-[15px] font-semibold transition-colors';

function LinhaItem({
  item,
  ativo,
  aoTocar,
}: {
  item: ItemMenu;
  ativo: boolean;
  aoTocar: () => void;
}) {
  const classe = `${ITEM_BASE} cursor-pointer ${
    item.perigo
      ? 'text-danger-600 hover:bg-danger-50'
      : ativo
        ? 'bg-primary-50 text-primary-700'
        : 'text-foreground-800 hover:bg-background-100'
  }`;
  const conteudo = (
    <>
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center ${
          item.perigo ? 'text-danger-500' : ativo ? 'text-primary-600' : 'text-foreground-500'
        }`}
      >
        <i className={`${item.icone} text-[20px]`} aria-hidden />
      </span>
      <span className="min-w-0 flex-1 truncate">{item.rotulo}</span>
      {item.novidade && (
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-label="Nova">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger-500 opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-danger-500" />
        </span>
      )}
      {item.selo && (
        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 px-1.5 text-[11px] font-bold text-white">
          {item.selo}
        </span>
      )}
      {!item.perigo && (
        <i className="ri-arrow-right-s-line text-lg text-foreground-300" aria-hidden />
      )}
    </>
  );

  const aoClicar = (e: React.MouseEvent) => {
    if (item.para) {
      e.preventDefault();
      aoTocar();
    } else {
      aoTocar();
    }
  };

  if (item.para) {
    return (
      <a href={item.para} onClick={aoClicar} className={classe}>
        {conteudo}
      </a>
    );
  }
  return (
    <button type="button" onClick={aoTocar} className={classe}>
      {conteudo}
    </button>
  );
}

export function MenuAppProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const acesso = useAcessoApp();
  const { convites } = useConvites();

  const [aberto, setAberto] = useState(false);
  const [avisosAberto, setAvisosAberto] = useState(false);
  const [funcaoAberto, setFuncaoAberto] = useState(false);
  const [codigoAberto, setCodigoAberto] = useState(false);
  const [sairAberto, setSairAberto] = useState(false);
  const [portariaAberto, setPortariaAberto] = useState(false);
  const [naoLidos, setNaoLidos] = useState(() => lerNaoLidos());
  const [oracaoNova, setOracaoNova] = useState(() => temOracaoNova());

  const painelRef = useRef<HTMLElement | null>(null);
  const arrasto = useRef<{ x: number; ativo: boolean }>({ x: 0, ativo: false });

  const iniciais = iniciaisNome(acesso.nome || acesso.primeiroNome || '');

  // Aviso novo (push) enquanto o app está aberto: soma no contador do ☰.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    const aoMensagem = (e: MessageEvent) => {
      if ((e.data as { tipo?: string } | null)?.tipo === 'seliga:aviso') {
        setNaoLidos((prev) => {
          const n = prev + 1;
          gravarNaoLidos(n);
          return n;
        });
      }
    };
    sw.addEventListener('message', aoMensagem);
    return () => sw.removeEventListener('message', aoMensagem);
  }, []);

  // Trava a rolagem do fundo enquanto a gaveta está aberta.
  useEffect(() => {
    if (!aberto) return;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [aberto]);

  const abrir = useCallback(() => {
    setOracaoNova(temOracaoNova());
    setAberto(true);
  }, []);
  const fechar = useCallback(() => setAberto(false), []);

  const ir = useCallback(
    (para: string) => {
      setAberto(false);
      navigate(para);
    },
    [navigate],
  );

  const marcarAvisosLidos = useCallback(() => {
    setNaoLidos(0);
    gravarNaoLidos(0);
  }, []);

  const sairDeVez = useCallback(async () => {
    setSairAberto(false);
    setAberto(false);
    try {
      // Sai SÓ neste celular (a conta pode ser compartilhada em outros).
      await supabase.auth.signOut({ scope: 'local' });
    } finally {
      navigate('/');
    }
  }, [navigate]);

  const abrirPortaria = useCallback(() => {
    if (acesso.portariaEventos.length === 1) {
      ir(`/portaria/${acesso.portariaEventos[0].evento_id}`);
    } else {
      setPortariaAberto(true);
    }
  }, [acesso.portariaEventos, ir]);

  const totalConvites = convites.length;
  const temFuncao =
    acesso.temBarraca || acesso.temCaixa || acesso.portariaEventos.length > 0;

  const ctx = useMemo<MenuAppCtx>(
    () => ({
      aberto,
      abrir,
      fechar,
      logado: acesso.logado,
      organiza: acesso.organiza,
      primeiroNome: acesso.primeiroNome,
      iniciais,
      naoLidos,
    }),
    [aberto, abrir, fechar, acesso.logado, acesso.organiza, acesso.primeiroNome, iniciais, naoLidos],
  );

  const grupos: GrupoMenu[] = [];

  const paraMim: ItemMenu[] = [
    { rotulo: 'Minhas inscrições', icone: 'ri-file-list-3-line', para: '/minhas-inscricoes' },
    {
      rotulo: 'Meus ingressos e fichas',
      icone: 'ri-ticket-2-line',
      para: '/meus-ingressos',
    },
    { rotulo: 'Minhas fichas', icone: 'ri-wallet-3-line', para: '/minhas-fichas' },
  ];
  if (acesso.temRifas) {
    paraMim.push({ rotulo: 'Ação entre amigos', icone: 'ri-coupon-3-line', para: '/minhas-rifas' });
  }
  if (totalConvites > 0) {
    paraMim.push({ rotulo: 'Convites', icone: 'ri-mail-line', para: '/convites', selo: String(totalConvites) });
  }
  paraMim.push({
    rotulo: 'Avisos',
    icone: 'ri-notification-3-line',
    aoTocar: () => {
      setAberto(false);
      marcarAvisosLidos();
      setAvisosAberto(true);
    },
    selo: naoLidos > 0 ? String(naoLidos) : undefined,
  });
  paraMim.push({ rotulo: 'Oração', icone: 'ri-heart-3-line', para: '/oracao', novidade: oracaoNova });
  paraMim.push({ rotulo: 'Liturgia de hoje', icone: 'ri-book-open-line', para: '/liturgia' });
  grupos.push({ titulo: 'Para mim', itens: paraMim });

  if (temFuncao) {
    const noEvento: ItemMenu[] = [];
    if (acesso.temBarraca) noEvento.push({ rotulo: 'Minha barraca', icone: 'ri-store-2-line', para: '/loja/barraca' });
    if (acesso.temCaixa) noEvento.push({ rotulo: 'Caixa', icone: 'ri-money-dollar-circle-line', para: '/caixa' });
    if (acesso.portariaEventos.length > 0) {
      noEvento.push({ rotulo: 'Portaria', icone: 'ri-qr-scan-2-line', aoTocar: abrirPortaria });
    }
    noEvento.push({
      rotulo: 'Entrar numa função',
      icone: 'ri-qr-scan-2-line',
      aoTocar: () => {
        setAberto(false);
        setFuncaoAberto(true);
      },
    });
    grupos.push({ titulo: 'No evento', itens: noEvento });
  }

  if (acesso.organiza) {
    const organizar: ItemMenu[] = [
      { rotulo: 'Meus eventos', icone: 'ri-calendar-check-line', para: '/meus-eventos' },
      { rotulo: 'Novo evento', icone: 'ri-add-line', para: '/criar' },
    ];
    if (acesso.adminPlataforma) {
      organizar.push({ rotulo: 'Painel da plataforma', icone: 'ri-shield-star-line', para: '/plataforma' });
    }
    grupos.push({ titulo: 'Organizar', itens: organizar });
  }

  const mais: ItemMenu[] = [];
  if (emApp()) {
    mais.push({
      rotulo: 'Tenho um código',
      icone: 'ri-qr-scan-2-line',
      aoTocar: () => {
        setAberto(false);
        setCodigoAberto(true);
      },
    });
  }
  mais.push(
    { rotulo: 'Início', icone: 'ri-home-5-line', para: '/' },
    { rotulo: 'Agenda', icone: 'ri-calendar-line', para: '/agenda' },
    { rotulo: 'Para organizadores', icone: 'ri-question-answer-line', para: '/conheca' },
  );
  if (!temFuncao) {
    mais.push({
      rotulo: 'Entrar numa função',
      icone: 'ri-qr-scan-2-line',
      aoTocar: () => {
        setAberto(false);
        setFuncaoAberto(true);
      },
    });
  }
  if (!acesso.organiza) {
    mais.push({ rotulo: 'Organizar um evento', icone: 'ri-add-line', para: '/conheca' });
  }
  if (acesso.logado) {
    mais.push({
      rotulo: 'Sair deste celular',
      icone: 'ri-logout-box-r-line',
      aoTocar: () => {
        setAberto(false);
        setSairAberto(true);
      },
      perigo: true,
    });
  }
  grupos.push({ titulo: 'Mais', itens: mais });

  const estaAtivo = (para?: string) =>
    !!para && (para === '/' ? pathname === '/' : pathname.startsWith(para));

  const aoPointerDown = (e: React.PointerEvent) => {
    arrasto.current = { x: e.clientX, ativo: true };
    if (painelRef.current) painelRef.current.style.transition = 'none';
  };
  const aoPointerMove = (e: React.PointerEvent) => {
    if (!arrasto.current.ativo || !painelRef.current) return;
    const dx = Math.max(0, e.clientX - arrasto.current.x);
    painelRef.current.style.transform = `translateX(${dx}px)`;
  };
  const aoPointerUp = (e: React.PointerEvent) => {
    if (!arrasto.current.ativo || !painelRef.current) return;
    const dx = Math.max(0, e.clientX - arrasto.current.x);
    arrasto.current.ativo = false;
    painelRef.current.style.transition = '';
    painelRef.current.style.transform = '';
    if (dx > 80) setAberto(false);
  };

  const gaveta =
    aberto && typeof document !== 'undefined'
      ? createPortal(
          <div className="fixed inset-0 z-[60]">
            <div
              className="animate-fade-in absolute inset-0 bg-black/40"
              onClick={fechar}
              aria-hidden
            />
            <aside
              ref={painelRef}
              role="dialog"
              aria-label="Menu"
              aria-modal="true"
              onPointerDown={aoPointerDown}
              onPointerMove={aoPointerMove}
              onPointerUp={aoPointerUp}
              className="animate-painel-entrar absolute right-0 top-0 flex h-full w-[86%] max-w-[340px] flex-col bg-background-50 shadow-2xl"
              style={{ touchAction: 'pan-y' }}
            >
              {/* Topo: iniciais + nome + ver perfil */}
              <div className="flex items-start gap-3 px-5 pb-4 pt-5">
                <AvatarPessoa tamanho={48} textoClassName="text-[16px]" />
                <div className="min-w-0 flex-1">
                  {acesso.logado ? (
                    <>
                      <p className="truncate text-[16px] font-extrabold text-foreground-950">
                        {acesso.primeiroNome || 'Você'}
                      </p>
                      <button
                        type="button"
                        onClick={() => ir('/meu-perfil')}
                        className="aperta cursor-pointer text-[13px] font-semibold text-primary-600"
                      >
                        Ver meu perfil
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="truncate text-[16px] font-extrabold text-foreground-950">
                        Bem-vindo
                      </p>
                      <button
                        type="button"
                        onClick={() => ir('/entrar')}
                        className="aperta cursor-pointer text-[13px] font-semibold text-primary-600"
                      >
                        Entrar na minha conta
                      </button>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={fechar}
                  aria-label="Fechar menu"
                  className="aperta -mr-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
                >
                  <i className="ri-close-line text-xl" aria-hidden />
                </button>
              </div>

              {/* Itens */}
              <div
                className="flex-1 overflow-y-auto px-3"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}
              >
                {grupos.map((grupo) => (
                  <div key={grupo.titulo} className="mb-2">
                    <p className="px-3 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wider text-foreground-400">
                      {grupo.titulo}
                    </p>
                    <div className="space-y-0.5">
                      {grupo.itens.map((item) => (
                        <LinhaItem
                          key={item.rotulo}
                          item={item}
                          ativo={estaAtivo(item.para)}
                          aoTocar={() => {
                            if (item.aoTocar) item.aoTocar();
                            else if (item.para) ir(item.para);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </div>,
          document.body,
        )
      : null;

  return (
    <Contexto.Provider value={ctx}>
      {children}
      {gaveta}

      {/* Avisos (o antigo sino): a folha com o bloco de avisos no celular. */}
      <Sheet aberto={avisosAberto} titulo="Avisos" aoFechar={() => setAvisosAberto(false)}>
        <AvisosCelular />
      </Sheet>

      <EntrarNaFuncao aberto={funcaoAberto} aoFechar={() => setFuncaoAberto(false)} />

      <FolhaTenhoCodigo aberto={codigoAberto} aoFechar={() => setCodigoAberto(false)} />

      <Modal aberto={portariaAberto} titulo="Portaria" aoFechar={() => setPortariaAberto(false)}>
        <p className="text-sm text-foreground-500">Escolha o evento para abrir a portaria.</p>
        <div className="mt-3 space-y-2">
          {acesso.portariaEventos.map((ev) => (
            <button
              key={ev.evento_id}
              type="button"
              onClick={() => {
                setPortariaAberto(false);
                ir(`/portaria/${ev.evento_id}`);
              }}
              className="aperta flex w-full cursor-pointer items-center justify-between gap-3 rounded-[16px] border border-background-200 bg-background-50 px-4 py-3 text-left transition-colors hover:bg-background-100"
            >
              <span className="text-[15px] font-semibold text-foreground-950">{ev.evento}</span>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden />
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        aberto={sairAberto}
        titulo="Sair deste celular?"
        aoFechar={() => setSairAberto(false)}
      >
        <p className="text-[15px] text-foreground-600">
          Você vai sair da sua conta só neste celular. Nos outros aparelhos a sessão continua
          aberta.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => setSairAberto(false)}
            className="aperta h-12 w-full cursor-pointer whitespace-nowrap rounded-[16px] border border-background-300 bg-background-50 text-[15px] font-semibold text-foreground-800 sm:flex-1"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={sairDeVez}
            className="aperta h-12 w-full cursor-pointer whitespace-nowrap rounded-[16px] bg-danger-500 text-[15px] font-semibold text-white sm:flex-1"
          >
            Sair
          </button>
        </div>
      </Modal>
    </Contexto.Provider>
  );
}